import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'app/atoms';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { IconDotsVertical } from '@tabler/icons-react-native';
import { RequestImage } from 'app/components/request-image';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { localeAwareFormatDistance } from 'app/lib/date';
import { isRequestIcon } from 'app/lib/is-request-icon';
import { createTheme as createDSTheme } from 'app/shopify';
import type { ProfileShare } from 'app/types/profile';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';

const theme = createDSTheme();

function ShareArtwork({ share, size }: { share: ProfileShare; size: number }) {
  const userInfo = useAtomValue(userInfoAtom);
  return (
    <View style={{ width: size, height: size + 12 }}>
      {isRequestIcon(share.listing.picture) || !share.listing.picture ? (
        <RequestImage width={size} height={size} isFound />
      ) : (
        <PCImage
          source={{ uri: share.listing.picture ?? undefined }}
          contentFit="cover"
          style={{
            width: size,
            height: size,
            borderRadius: 14,
            backgroundColor: theme.colors['surface.tertiary-neutral'],
          }}
        />
      )}
      {isFeatureEnabled(FeatureFlag.rateAfterLoans, userInfo?.featureFlags) && share.hasPositiveCounterpartRating ? (
        <View
          style={{
            position: 'absolute',
            left: 8,
            top: 6,
            width: 32,
            height: 32,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 16,
            backgroundColor: '#B8ECE0',
          }}
        >
          <Text fontSize={18} lineHeight={22}>
            👍
          </Text>
        </View>
      ) : null}
      <View
        style={{
          position: 'absolute',
          bottom: 0,
          left: (size - 48) / 2,
          borderRadius: 26,
          borderWidth: 1,
          borderColor: theme.colors['surface.default'],
        }}
      >
        <Thumbnail height={48} source={{ uri: share.counterpart.picture ?? undefined }} />
      </View>
    </View>
  );
}

export function ShareActivityCard({ share, onPress }: { share: ProfileShare; onPress: () => void }) {
  const { t } = useTranslation('neighbour-profile');
  const name = [share.counterpart.firstName, share.counterpart.lastName].filter(Boolean).join(' ');
  return (
    <Pressable onPress={onPress} style={{ width: 120 }}>
      <ShareArtwork share={share} size={120} />
      <Text fontSize={12} lineHeight={14} textAlign="center" numberOfLines={3} style={{ marginTop: 4 }}>
        <Text fontSize={12} lineHeight={14} fontWeight="700">
          {name}{' '}
        </Text>
        {t(share.subjectRole === 'lender' ? 'shares.borrowed' : 'shares.lent')}{' '}
        <Text fontSize={12} lineHeight={14} fontWeight="700">
          {share.listing.title}
        </Text>
      </Text>
      <Text color="text.tertiary" fontSize={12} lineHeight={14} textAlign="center">
        {localeAwareFormatDistance(new Date(), new Date(share.sharedAt))}
      </Text>
    </Pressable>
  );
}

export function ShareHistoryRow({
  share,
  onPress,
  onMore,
}: {
  share: ProfileShare;
  onPress: () => void;
  onMore: () => void;
}) {
  const { t } = useTranslation('neighbour-profile');
  const counterpartName = [share.counterpart.firstName, share.counterpart.lastName].filter(Boolean).join(' ');
  return (
    <Row alignItems="center" minHeight={112}>
      <Pressable accessibilityRole="button" onPress={onPress} style={{ flex: 1 }}>
        <Row alignItems="center" gap="spacing.general.x4">
          <ShareArtwork share={share} size={88} />
          <Column flex={1}>
            <Text variant="bodySmall">
              <Text fontWeight="700" variant="bodySmall">
                {counterpartName}{' '}
              </Text>
              {t(share.subjectRole === 'lender' ? 'shares.borrowed' : 'shares.lent')}{' '}
              <Text fontWeight="700" variant="bodySmall">
                {share.listing.title}
              </Text>
            </Text>
            <Text color="text.tertiary" variant="bodySmall">
              {localeAwareFormatDistance(new Date(), new Date(share.sharedAt))}
            </Text>
          </Column>
        </Row>
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('shares.memberActions.more', { name: counterpartName })}
        onPress={onMore}
        style={{ width: 44, minHeight: 48, alignItems: 'center', justifyContent: 'center' }}
      >
        <IconDotsVertical color={theme.colors['text.primary']} size={24} strokeWidth={ICON_STROKE_WIDTH} />
      </Pressable>
    </Row>
  );
}
